// ── Welle K6: „Dein Tag“ am PC ──
// Links, was jetzt zählt (Pill, große Zeile, ein Satz, Knöpfe je Zustand), darunter der Tag als
// Zeitleiste mit Klassenbuch-Status; rechts eine weiße Karte mit dem, was als Nächstes kommt.
// Nur bestehende Endpunkte: /api/heute/dein-tag (Zustand, Stunden), /api/klassenbuch je Klasse
// (Thema eingetragener Stunden), /api/stundenplan + Zeitraster (Stunden des nächsten Schultags),
// /api/schultermine (Abendtermine). Für die Stunde nach der laufenden liefert dein-tag keine
// letzte Stunde und Hausaufgabe — die Karte „Als Nächstes“ zeigt sie deshalb nicht.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { apiGet } from '../../../lib/api';
import { globalNav, oeffneKlassenbuchNachtragen } from '../../../lib/nav';
import { useZeitraster, zeitZuMin } from '../../../lib/zeitraster';
import type { RasterEintrag } from '../../../lib/zeitraster';
import { useKlassenbuchSprung } from './sprung';
import { useLader } from './lader';
import { useWidgetGroesse } from './groesse';
import type { DeinTagDaten, StundeInfo, useDeinTag } from './DeinTag';

type SpZeile = { wochentag: number | string; stunde: number; fach: string; klasse_id: number | null; klasse_name: string | null; raum?: string | null };
type Termin = { id: number; datum: string; titel: string };
type Status = 'eingetragen' | 'offen' | 'laeuft' | 'kommt';
type Kachel = { nr: number; beginn: string; frei: boolean; titel: string; thema?: string | null; status?: Status; klick?: () => void };
type Zeile = { zeit: string; text: string; hervor?: boolean };
type Karte = {
  kopf: string; zeile: string; klasse?: string | null; fach?: string | null;
  letzte?: string | null; ha?: string | null; hinweis?: string | null;
  liste: Zeile[]; abend: Zeile[]; schluss: string | null; link: { label: string; ziel: () => void };
  kurz: string;                                   // eine Zeile, wenn das Widget schmaler als 3 Spalten ist
};

const datumObj = (d: string) => new Date(d + 'T12:00:00');
const wochentagLang = (d: string) => datumObj(d).toLocaleDateString('de-DE', { weekday: 'long' });
const ttmm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.`;
const plusTage = (d: string, n: number) => { const x = datumObj(d); x.setDate(x.getDate() + n); return x.toLocaleDateString('sv-SE'); };
const raumText = (r: string | null | undefined) => !r?.trim() ? null : /^(r\b|raum\b)/i.test(r.trim()) ? r.trim() : `Raum ${r.trim()}`;
const lektion = (klasse: string | null | undefined, fach: string | null | undefined) => [klasse, fach].filter(Boolean).join(' ');

/** Stunden des statischen Plans an einem (bereits geprüften) Schultag, mit Zeiten aus dem Raster */
function stundenAm(sp: SpZeile[] | undefined, datum: string, raster: RasterEintrag[]) {
  if (!sp) return null;
  const wt = ((datumObj(datum).getDay() + 6) % 7) + 1;
  const proStunde = new Map<number, SpZeile>();
  for (const r of sp) if (Number(r.wochentag) === wt && !proStunde.has(r.stunde)) proStunde.set(r.stunde, r);
  return Array.from(proStunde.values())
    .map(r => ({ ...r, zeit: raster.find(z => z.stunde === r.stunde) }))
    .filter((r): r is SpZeile & { zeit: RasterEintrag } => !!r.zeit)
    .sort((a, b) => a.stunde - b.stunde);
}

/** Thema der eingetragenen Stunden eines Tages (je Klasse eine Anfrage an /api/klassenbuch) */
function useThemen(d: DeinTagDaten | undefined) {
  const [themen, setThemen] = useState<Record<number, string | null>>({});
  const klassen = useMemo(() => Array.from(new Set((d?.heute ?? []).filter(h => h.eingetragen && h.klasse_id).map(h => Number(h.klasse_id)))).sort(), [d]);
  const schluessel = `${d?.datum ?? ''}|${klassen.join(',')}|${(d?.heute ?? []).filter(h => h.eingetragen).map(h => h.stunde_nr).join(',')}`;
  useEffect(() => {
    if (!d || !klassen.length) { setThemen({}); return; }
    let aktiv = true;
    Promise.all(klassen.map(k => apiGet<{ stunde: number; thema: string | null; klasse_id: number }[]>(`/api/klassenbuch?klasse_id=${k}&datum=${d.datum}`).catch(() => [])))
      .then(listen => {
        if (!aktiv) return;
        const neu: Record<number, string | null> = {};
        for (const h of d.heute) {
          if (!h.eingetragen || !h.klasse_id) continue;
          const zeile = listen.flat().find(e => Number(e.klasse_id) === Number(h.klasse_id) && Number(e.stunde) === h.stunde_nr);
          neu[h.stunde_nr] = zeile?.thema?.trim() || null;
        }
        setThemen(neu);
      });
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);
  return themen;
}

export function DeinTagPc({ g }: { g: ReturnType<typeof useDeinTag> }) {
  const gr = useWidgetGroesse();
  // Karte rechts nur, wenn genug Platz ist (3 Spalten und mindestens 600 px; sonst eine Zeile)
  const wurzel = useRef<HTMLDivElement | null>(null);
  const [px, setPx] = useState(0);
  useEffect(() => {
    const el = wurzel.current; if (!el) return;
    const ro = new ResizeObserver(() => setPx(el.clientWidth));
    ro.observe(el); setPx(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const breit = gr.w >= 3 && (px === 0 || px >= 600);
  const sprung = useKlassenbuchSprung();
  const zr = useZeitraster();
  const d = g.daten!;
  const nd = d.naechster_schultag;
  const sp = useLader<SpZeile[]>(nd ? '/api/stundenplan' : null);
  const tm = useLader<Termin[]>('/api/schultermine');
  const themen = useThemen(d);
  const m = g.jetztMin;

  const echt = d.heute.filter(h => !h.frei);
  const schlussHeute = echt.length ? echt[echt.length - 1].ende : null;
  const abendHeute: Zeile[] = (tm.daten ?? [])
    .filter(t => t.datum.length > 10 && t.datum.slice(0, 10) === d.datum && zeitZuMin(t.datum.slice(11, 16)) >= Math.max(m, schlussHeute ? zeitZuMin(schlussHeute) : 0))
    .sort((a, b) => a.datum.localeCompare(b.datum)).slice(0, 2)
    .map(t => ({ zeit: t.datum.slice(11, 16), text: t.titel }));

  // ── Zeitleiste ──
  const laufend = d.zustand === 'in_stunde' ? d.fokus?.stunde_nr ?? null : null;
  let tagLabel = `Dein ${wochentagLang(d.datum)}`;
  let kacheln: Kachel[] = d.heute.map(h => {
    if (h.frei) return { nr: h.stunde_nr, beginn: h.beginn, frei: true, titel: 'frei' };
    const status: Status = h.stunde_nr === laufend ? 'laeuft' : h.eingetragen ? 'eingetragen' : h.vorbei ? 'offen' : 'kommt';
    const thema = status === 'eingetragen' ? (themen[h.stunde_nr] ?? '') : status === 'offen' ? 'noch kein Eintrag' : status === 'laeuft' ? 'jetzt' : undefined;
    return { nr: h.stunde_nr, beginn: h.beginn, frei: false, titel: lektion(h.klasse_name, h.fach), thema, status, klick: () => sprung(d.datum, h.stunde_nr, h.klasse_id) };
  });
  const ndStunden = nd ? stundenAm(sp.daten, nd.datum, zr.raster) : null;
  if (d.zustand === 'schulfrei' && nd && ndStunden?.length) {
    tagLabel = `Dein ${wochentagLang(nd.datum)}, ${ttmm(nd.datum)}`;
    kacheln = [];
    for (let nr = ndStunden[0].stunde; nr <= ndStunden[ndStunden.length - 1].stunde; nr++) {
      const s = ndStunden.find(x => x.stunde === nr);
      const z = zr.raster.find(x => x.stunde === nr);
      if (!z) continue;
      kacheln.push(s ? { nr, beginn: z.beginn, frei: false, titel: lektion(s.klasse_name, s.fach), status: 'kommt' } : { nr, beginn: z.beginn, frei: true, titel: 'frei' });
    }
  }
  // schmal (2 Spalten): höchstens 4 Kacheln ab der laufenden bzw. ersten nicht vergangenen Stunde
  if (!breit && kacheln.length > 4) {
    const ab = Math.max(0, kacheln.findIndex(k => k.status === 'laeuft' || k.status === 'kommt'));
    kacheln = kacheln.slice(Math.min(ab, kacheln.length - 4), Math.min(ab, kacheln.length - 4) + 4);
  }

  // ── Karte rechts ──
  const karteNaechsterTag = (kopf: string): Karte | null => {
    if (!nd) return null;
    const e = nd.erste_stunde;
    const morgen = nd.datum === plusTage(d.datum, 1);
    const tag = morgen ? wochentagLang(nd.datum) : `${wochentagLang(nd.datum)}, ${ttmm(nd.datum)}`;
    const liste = (ndStunden ?? []).map(s => ({ zeit: s.zeit.beginn, text: lektion(s.klasse_name, s.fach), hervor: s.stunde === e.stunde_nr }));
    return {
      kopf, zeile: `${tag}, ${e.stunde_nr}. Stunde, ${e.beginn}`, klasse: e.klasse_name, fach: e.fach,
      letzte: e.letzte_stunde?.thema ?? null, ha: e.hausaufgabe?.text ?? null,
      liste: liste.length > 1 ? liste : [], abend: [],
      schluss: ndStunden?.length ? ndStunden[ndStunden.length - 1].zeit.ende : null,
      link: { label: 'Stundenplan', ziel: () => globalNav('stundenplan') },
      kurz: `${kopf}, ${e.beginn}: ${lektion(e.klasse_name, e.fach)}`,
    };
  };
  let karte: Karte | null = null;
  if (d.zustand === 'nach_unterricht') {
    karte = karteNaechsterTag(nd && nd.datum === plusTage(d.datum, 1) ? 'Morgen' : 'Nächster Schultag');
  } else if (d.zustand === 'schulfrei') {
    karte = karteNaechsterTag(d.grund === 'ferien' ? 'Erster Schultag' : nd && nd.datum === plusTage(d.datum, 1) ? 'Morgen' : 'Nächster Schultag');
  } else if (d.zustand === 'in_stunde') {
    const nach = d.heute.find(h => !h.frei && h.stunde_nr > (laufend ?? 0));
    karte = {
      kopf: 'Als Nächstes',
      zeile: nach ? `${nach.stunde_nr}. Stunde, ${nach.beginn}` : `Heute, ${ttmm(d.datum)}`,
      klasse: nach?.klasse_name, fach: nach?.fach,
      hinweis: nach ? raumText(nach.raum) : 'Danach hast du heute keinen Unterricht mehr.',
      liste: [], abend: abendHeute, schluss: schlussHeute,
      link: { label: 'Stundenplan', ziel: () => globalNav('stundenplan') },
      kurz: nach ? `Als Nächstes, ${nach.beginn}: ${lektion(nach.klasse_name, nach.fach)}` : 'Danach kein Unterricht mehr',
    };
  } else if (d.fokus) {
    // vor dem Unterricht, Pause, Freistunde: der Rest des Tages ab der nächsten Stunde
    const rest = echt.filter(h => h.stunde_nr >= d.fokus!.stunde_nr);
    karte = {
      kopf: 'Heute noch', zeile: `${wochentagLang(d.datum)}, ${ttmm(d.datum)}`,
      liste: rest.map(h => ({ zeit: h.beginn, text: lektion(h.klasse_name, h.fach) })),
      abend: abendHeute, schluss: schlussHeute,
      link: { label: 'Kalender', ziel: () => globalNav('kalender') },
      kurz: `Heute noch ${rest.length} ${rest.length === 1 ? 'Stunde' : 'Stunden'}${schlussHeute ? `, Schluss ${schlussHeute}` : ''}`,
    };
  }

  // ── Links: Pill, große Zeile, Satz, Knöpfe ──
  let pill: { icon: string; text: string };
  let gross: React.ReactNode;
  let satz: string | null = null;
  let chips: StundeInfo | null = null;
  const knoepfe: { label: string; icon: string; haupt?: boolean; onClick: () => void }[] = [];
  if (d.zustand === 'nach_unterricht') {
    pill = { icon: 'check_circle', text: 'Unterricht vorbei' };
    gross = 'Für heute geschafft.';
    const n = echt.filter(h => h.klasse_id).length;
    satz = d.offen_heute > 0
      ? `${d.offen_heute} ${d.offen_heute === 1 ? 'Stunde' : 'Stunden'} von heute ${d.offen_heute === 1 ? 'hat' : 'haben'} noch keinen Klassenbuch-Eintrag.`
      : n === 1 ? 'Die Stunde ist im Klassenbuch eingetragen.' : `Alle ${n} Stunden sind im Klassenbuch eingetragen.`;
    const offen = d.heute.find(h => !h.frei && h.vorbei && !h.eingetragen && h.klasse_id);
    if (d.offen_heute > 0 && offen) knoepfe.push({ label: 'Jetzt nachtragen', icon: 'edit', haupt: true, onClick: () => sprung(d.datum, offen.stunde_nr, offen.klasse_id) });
    if (d.offen_heute > 0) knoepfe.push({ label: 'Per Sprache', icon: 'mic', onClick: () => oeffneKlassenbuchNachtragen() });
  } else if (d.zustand === 'schulfrei') {
    const f = d.grund === 'ferien' && d.ferien;
    pill = f ? { icon: 'beach_access', text: d.ferien!.name } : d.grund === 'wochenende' ? { icon: 'weekend', text: 'Wochenende' }
      : d.grund === 'feiertag' ? { icon: 'celebration', text: 'Feiertag' } : { icon: 'event_busy', text: 'Kein Unterricht' };
    gross = f ? 'Schöne Ferien.' : d.grund === 'wochenende' ? 'Schönes Wochenende.' : 'Heute kein Unterricht.';
    const weiter = nd ? `${wochentagLang(nd.datum)}, ${ttmm(nd.datum)}` : null;
    satz = f ? `Die ${d.ferien!.name} gehen bis ${wochentagLang(d.ferien!.bis)}, ${ttmm(d.ferien!.bis)}${weiter ? ' Danach geht es so los:' : ''}`
      : weiter ? `Am ${weiter} geht es weiter.` : 'Im Kalender ist gerade kein weiterer Unterricht eingetragen.';
  } else {
    const f = d.fokus!;
    const laeuft = d.zustand === 'in_stunde';
    const bis = laeuft ? zeitZuMin(f.ende) - m : zeitZuMin(f.beginn) - m;
    pill = laeuft ? { icon: 'play_arrow', text: `Läuft, noch ${Math.max(0, bis)} Min.` }
      : { icon: 'schedule', text: bis >= 60 ? `Um ${f.beginn}` : bis <= 0 ? 'Beginnt jetzt' : bis === 1 ? 'In 1 Minute' : `In ${bis} Minuten` };
    gross = f.klasse_name ? <>{f.klasse_name} <span>{f.fach}</span></> : f.fach;
    satz = [`${f.stunde_nr}. Stunde, ${f.beginn} bis ${f.ende}`, raumText(f.raum)].filter(Boolean).join(', ');
    chips = f;
    if (laeuft) {
      knoepfe.push({ label: 'Stunde eintragen', icon: 'edit', haupt: true, onClick: () => sprung(d.datum, f.stunde_nr, f.klasse_id) });
      knoepfe.push({ label: 'Per Sprache', icon: 'mic', onClick: () => oeffneKlassenbuchNachtragen() });
    } else {
      knoepfe.push({ label: 'Klassenbuch öffnen', icon: 'menu_book', haupt: true, onClick: () => sprung(d.datum, f.stunde_nr, f.klasse_id) });
    }
  }

  const ls = chips?.letzte_stunde, ha = chips?.hausaufgabe;
  return (
    <div ref={wurzel} className={`ld-dtp${breit ? ' ist-breit' : ''}`}>
      <div className="ld-dtp__links">
        <span className="ld-dtp__pill"><MIcon name={pill.icon} size={14} />{pill.text}</span>
        <div className="ld-dtp__gross">{gross}</div>
        {satz && <p className="ld-dtp__satz">{satz}</p>}
        {chips && (ls?.thema || ha) && (
          <div className="ld-dtp__chips">
            {ls?.thema && (
              <button type="button" className="ld-dtp__chip" onClick={() => sprung(ls.datum, ls.stunde, chips!.klasse_id)}>
                <MIcon name="history" size={14} /><span>Letzte Stunde: {ls.thema}</span>
              </button>
            )}
            {ha && (
              <button type="button" className="ld-dtp__chip" onClick={() => sprung(ha.datum, ha.stunde, chips!.klasse_id)}>
                <MIcon name="menu_book" size={14} /><span>Hausaufgabe: {ha.text}</span>
              </button>
            )}
          </div>
        )}
        {knoepfe.length > 0 && (
          <div className="ld-dtp__knoepfe">
            {knoepfe.map(k => (
              <button key={k.label} type="button" className={`ld-dtp__knopf${k.haupt ? ' ist-haupt' : ''}`} onClick={k.onClick}>
                <MIcon name={k.icon} size={16} />{k.label}
              </button>
            ))}
          </div>
        )}
        {!breit && karte && <p className="ld-dtp__kurz"><MIcon name="arrow_forward" size={14} />{karte.kurz}</p>}
      </div>

      {kacheln.length > 0 && (
        <div className="ld-dtp__tag">
          <div className="ld-dtp__taglabel">{tagLabel}</div>
          <div className="ld-dtp__tl" role="list" aria-label={tagLabel}>
            {kacheln.map(k => k.frei ? (
              <div key={k.nr} role="listitem" className="ld-dtp__k ist-frei"><small>{k.nr}.</small><b>frei</b></div>
            ) : (
              React.createElement(k.klick ? 'button' : 'div', {
                key: k.nr, role: 'listitem', className: `ld-dtp__k ist-${k.status}`,
                ...(k.klick ? { type: 'button', onClick: k.klick } : {}),
              },
                <small>{k.nr}. · {k.beginn}</small>,
                <b>{k.titel}</b>,
                k.thema !== undefined ? <span className="ld-dtp__thema">{k.thema}</span> : <span className="ld-dtp__thema" aria-hidden="true" />,
                <em className="ld-dtp__status">
                  {k.status === 'eingetragen' ? <><MIcon name="check" size={12} /><span className="ld-dtp__statustext">eingetragen</span></> : k.status === 'offen' ? <><i />offen</> : k.status === 'laeuft' ? 'läuft' : 'kommt'}
                </em>)
            ))}
          </div>
        </div>
      )}

      {breit && karte && (
        <aside className="ld-dtp__karte" aria-label={karte.kopf}>
          <span className="ld-dtp__kk">{karte.kopf}</span>
          <span className="ld-dtp__kz">{karte.zeile}</span>
          {(karte.klasse || karte.fach) && <div className="ld-dtp__kl">{karte.klasse} <span>{karte.fach}</span></div>}
          {karte.hinweis && <p className="ld-dtp__khinweis">{karte.hinweis}</p>}
          {karte.letzte && <div className="ld-dtp__kzeile"><MIcon name="history" size={15} /><div><b>Letzte Stunde</b>{karte.letzte}</div></div>}
          {karte.ha && <div className="ld-dtp__kzeile"><MIcon name="menu_book" size={15} /><div><b>Hausaufgabe</b>{karte.ha}</div></div>}
          {(karte.liste.length > 0 || karte.abend.length > 0) && (
            <div className="ld-dtp__kliste">
              {karte.liste.map(z => <div key={z.zeit + z.text} className={z.hervor ? 'ist-hervor' : ''}><span>{z.zeit}</span>{z.text}</div>)}
              {karte.abend.length > 0 && karte.liste.length > 0 && <hr />}
              {karte.abend.map(z => <div key={z.zeit + z.text} className="ist-termin"><span>{z.zeit}</span>{z.text}</div>)}
            </div>
          )}
          <div className="ld-dtp__kfuss">
            <span>{karte.schluss ? `Schluss ${karte.schluss}` : ''}</span>
            <button type="button" onClick={karte.link.ziel}>{karte.link.label}</button>
          </div>
        </aside>
      )}
    </div>
  );
}
